import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaGlobeAfrica, FaLanguage, FaUsers, FaMoneyBillWave, FaLandmark, FaPaw, FaMountain, FaUmbrellaBeach, FaHiking, FaSuitcase, FaPassport, FaSimCard, FaCreditCard, FaStar, FaWater } from "react-icons/fa";
import "../css/styles.css";

const Tanzania = () => {
  return (
    <div className="tanzania-page">
      {/* ===== HERO ===== */}
      <section className="tanzania-hero">
        <div className="tanzania-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776345012/Tanzania_mskzqt.jpg"
            alt="Tanzania Safari Landscape"
          />
          <div className="tanzania-hero-overlay" />
        </div>
        <div className="tanzania-hero-content">
          <span className="tanzania-hero-eyebrow">Discover East Africa</span>
          <h1>Tanzania</h1>
          <p className="tanzania-hero-sub">
            Vast wilderness areas, extraordinary wildlife, and dramatic landscapes — from the endless plains of the Serengeti to the volcanic beauty of the Ngorongoro Crater.
          </p>
          <div className="tanzania-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Design Your Trip <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/blogs" className="hero-cta secondary">
              Continue Exploring
            </Link>
          </div>
        </div>
      </section>

      {/* ===== INTRODUCTION ===== */}
      <section className="tanzania-intro">
        <div className="tanzania-section-header">
          <span className="section-tag">Introduction</span>
          <h2>Africa's Wild Heart</h2>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-intro-text">
          <p>
            From the endless plains of the Serengeti to the volcanic beauty of the Ngorongoro Crater and the giant baobabs of Tarangire, Tanzania offers some of the most iconic safari experiences on the continent.
          </p>
          <p>
            Home to Africa's highest mountain, Mount Kilimanjaro, and some of the largest national parks in the world, Tanzania is a destination defined by scale and natural beauty.
          </p>
          <p>
            Travelers come to Tanzania for its incredible wildlife, remote landscapes, and authentic safari experiences. Here, it is still possible to explore vast protected areas where nature remains largely untouched.
          </p>
          <p>
            Many journeys through Tanzania end with a relaxing escape to the tropical island of Zanzibar, creating the perfect combination of adventure and relaxation.
          </p>
        </div>
      </section>

      {/* ===== GENERAL INFORMATION ===== */}
      <section className="tanzania-info">
        <div className="tanzania-section-header">
          <span className="section-tag centered">General Information</span>
          <h2>About Tanzania</h2>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-info-grid">
          <div className="tanzania-info-card">
            <div className="tanzania-info-icon"><FaMapMarkerAlt /></div>
            <h3>Location</h3>
            <p>Located in East Africa, bordering Kenya, Uganda, Rwanda, Burundi, the Democratic Republic of Congo, Mozambique, Zambia, Malawi, and the Indian Ocean. Landscapes range from vast savannah plains and volcanic highlands to tropical coastlines and mountain ranges.</p>
          </div>
          <div className="tanzania-info-card">
            <div className="tanzania-info-icon"><FaLandmark /></div>
            <h3>History</h3>
            <p>Centuries of cultural exchange shaped Tanzania's coastal trading towns. After German and British colonial rule, the country gained independence in 1961. Tanganyika and Zanzibar united in 1964 to form modern Tanzania — one of Africa's most stable nations.</p>
          </div>
          <div className="tanzania-info-card">
            <div className="tanzania-info-icon"><FaLanguage /></div>
            <h3>Languages</h3>
            <p>Official languages are Swahili and English. Swahili is widely spoken as a unifying national language, while English is commonly used in tourism, education, and business.</p>
          </div>
          <div className="tanzania-info-card">
            <div className="tanzania-info-icon"><FaUsers /></div>
            <h3>Population</h3>
            <p>Over 65 million people and home to more than 120 ethnic groups, making it one of Africa's most culturally diverse nations.</p>
          </div>
          <div className="tanzania-info-card">
            <div className="tanzania-info-icon"><FaMoneyBillWave /></div>
            <h3>Currency</h3>
            <p>Tanzanian Shilling (TZS). US dollars are widely accepted in tourism areas, particularly for hotels, lodges, and safari services.</p>
          </div>
          <div className="tanzania-info-card">
            <div className="tanzania-info-icon"><FaGlobeAfrica /></div>
            <h3>Religion</h3>
            <p>Religiously diverse with large Christian and Muslim populations, along with traditional beliefs. Islam is particularly prominent along the coast and in Zanzibar.</p>
          </div>
        </div>
      </section>

      {/* ===== UNIQUE FACTS ===== */}
      <section className="tanzania-facts">
        <div className="tanzania-section-header">
          <span className="section-tag">Did You Know?</span>
          <h2>Unique Facts About Tanzania</h2>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-facts-list">
          <div className="tanzania-fact-item">
            <FaStar className="tanzania-fact-icon" />
            <p>Home to Mount Kilimanjaro — Africa's highest mountain.</p>
          </div>
          <div className="tanzania-fact-item">
            <FaStar className="tanzania-fact-icon" />
            <p>Contains some of the largest national parks in Africa.</p>
          </div>
          <div className="tanzania-fact-item">
            <FaStar className="tanzania-fact-icon" />
            <p>The Serengeti ecosystem hosts the Great Migration of over two million animals.</p>
          </div>
          <div className="tanzania-fact-item">
            <FaStar className="tanzania-fact-icon" />
            <p>The Ngorongoro Crater is the world's largest intact volcanic caldera.</p>
          </div>
          <div className="tanzania-fact-item">
            <FaStar className="tanzania-fact-icon" />
            <p>Tanzania protects nearly 30% of its land as national parks or conservation areas.</p>
          </div>
        </div>
      </section>

      {/* ===== WHY TRAVEL TO TANZANIA ===== */}
      <section className="tanzania-why">
        <div className="tanzania-why-content">
          <div className="tanzania-section-header">
            <span className="section-tag">Why Tanzania?</span>
            <h2>Why Travel to Tanzania</h2>
            <div className="tanzania-section-line" />
          </div>
          <p>
            Tanzania offers one of the most authentic safari experiences in Africa. The country's vast protected landscapes allow wildlife to roam freely across enormous ecosystems, creating safari experiences that feel raw, dramatic, and truly wild.
          </p>
          <p>
            The Serengeti's endless plains host one of the greatest wildlife spectacles on Earth — the Great Migration — where millions of wildebeest and zebras move across the savannah in search of fresh grazing.
          </p>
          <p>
            Beyond the famous parks, Tanzania offers remarkable diversity: ancient volcanic craters filled with wildlife, baobab-dotted landscapes, remote river systems, and some of Africa's most untouched wilderness areas.
          </p>
          <p>
            For travelers seeking iconic wildlife encounters, dramatic scenery, and immersive safaris, Tanzania delivers unforgettable experiences.
          </p>
        </div>
      </section>

      {/* ===== PEOPLE & CULTURE ===== */}
      <section className="tanzania-culture">
        <div className="tanzania-section-header">
          <span className="section-tag centered">Heritage</span>
          <h2>People &amp; Culture</h2>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-culture-content">
          <p>
            Tanzania is one of Africa's most culturally diverse countries, with more than 120 ethnic groups living across the nation.
          </p>
          <p>
            Swahili culture plays a central role in shaping Tanzanian identity, particularly along the coast and in historic trading towns. The Swahili language itself developed through centuries of interaction between African, Arab, and Persian traders.
          </p>
          <p>
            Among the country's most well-known communities are the Maasai, whose semi-nomadic lifestyle and colorful traditions remain an important part of East African culture.
          </p>
          <p>
            Across Tanzania, music, storytelling, traditional crafts, and community life remain deeply woven into daily life.
          </p>
          <p className="tanzania-karibu">
            Visitors are often welcomed with the Swahili phrase <strong>"Karibu sana"</strong> — a warm invitation meaning "You are very welcome."
          </p>
        </div>
      </section>

      {/* ===== EXPERIENCES ===== */}
      <section className="tanzania-experiences">
        <div className="tanzania-section-header">
          <span className="section-tag centered">Experiences</span>
          <h2>Experiences in Tanzania</h2>
          <p className="tanzania-section-sub">From classic wildlife safaris to cultural exploration and adventure.</p>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-exp-grid">
          <div className="tanzania-exp-card">
            <div className="tanzania-exp-icon"><FaPaw /></div>
            <h3>Wildlife Safaris</h3>
            <p>One of the best places in Africa to experience wildlife in its natural habitat. Large ecosystems and well-protected parks allow animals to move freely across vast landscapes.</p>
          </div>
          <div className="tanzania-exp-card">
            <div className="tanzania-exp-icon"><FaWater /></div>
            <h3>The Great Migration</h3>
            <p>One of the greatest wildlife spectacles on Earth — millions of wildebeest and zebras migrate through the Serengeti, creating dramatic river crossings and extraordinary predator encounters.</p>
          </div>
          <div className="tanzania-exp-card">
            <div className="tanzania-exp-icon"><FaGlobeAfrica /></div>
            <h3>Cultural Encounters</h3>
            <p>Learn about traditional lifestyles and cultures through visits to local communities, markets, and cultural centers across the country.</p>
          </div>
          <div className="tanzania-exp-card">
            <div className="tanzania-exp-icon"><FaMountain /></div>
            <h3>Adventure Travel</h3>
            <p>Trek Mount Kilimanjaro, Africa's highest mountain. Explore walking safaris, hiking trails, and remote wilderness areas for the ultimate adventure.</p>
          </div>
          <div className="tanzania-exp-card">
            <div className="tanzania-exp-icon"><FaUmbrellaBeach /></div>
            <h3>Safari &amp; Island Combos</h3>
            <p>Combine your Tanzanian safari with time on the tropical island of Zanzibar — the perfect balance of adventure and relaxation.</p>
          </div>
        </div>
      </section>

      {/* ===== SAFARI DESTINATIONS ===== */}
      <section className="tanzania-destinations">
        <div className="tanzania-section-header">
          <span className="section-tag centered">Safari Destinations</span>
          <h2>Where to Go on Safari</h2>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-dest-grid">
          <div className="tanzania-dest-card">
            <div className="tanzania-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Serengeti National Park</h3>
            </div>
            <p>One of the world's most famous wildlife destinations. Its endless plains host the Great Migration and offer some of the best predator sightings in Africa.</p>
          </div>
          <div className="tanzania-dest-card">
            <div className="tanzania-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Ngorongoro Crater</h3>
            </div>
            <p>Formed from a collapsed volcano, the Ngorongoro Crater is a unique ecosystem filled with wildlife, including lions, elephants, rhinos, and large herds of grazing animals.</p>
          </div>
          <div className="tanzania-dest-card">
            <div className="tanzania-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Tarangire National Park</h3>
            </div>
            <p>Known for its massive elephant herds and ancient baobab trees. During the dry season, the Tarangire River attracts large concentrations of wildlife.</p>
          </div>
          <div className="tanzania-dest-card">
            <div className="tanzania-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Nyerere National Park (Selous)</h3>
            </div>
            <p>One of Africa's largest protected areas, offering a remote safari experience with boat safaris, walking safaris, and diverse wildlife.</p>
          </div>
          <div className="tanzania-dest-card">
            <div className="tanzania-dest-card-header">
              <FaMapMarkerAlt />
              <h3>Ruaha National Park</h3>
            </div>
            <p>One of Tanzania's most remote and wild national parks, known for its dramatic landscapes and large predator populations.</p>
          </div>
        </div>
      </section>

      {/* ===== HOW SAFARI WORKS ===== */}
      <section className="tanzania-how">
        <div className="tanzania-section-header">
          <span className="section-tag centered">Plan With Confidence</span>
          <h2>How a Tanzania Safari Works</h2>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-how-grid">
          <div className="tanzania-how-card">
            <div className="tanzania-how-number">01</div>
            <h3>Safari Lodges &amp; Camps</h3>
            <p>Accommodations range from comfortable lodges to luxurious tented camps in or near national parks. Many operate on an all-inclusive basis with meals, guided game drives, and safari activities.</p>
          </div>
          <div className="tanzania-how-card">
            <div className="tanzania-how-number">02</div>
            <h3>Game Drives</h3>
            <p>Conducted in specially designed 4x4 safari vehicles with experienced guides. Most safaris include morning and afternoon game drives when wildlife is most active.</p>
          </div>
          <div className="tanzania-how-card">
            <div className="tanzania-how-number">03</div>
            <h3>Getting Around</h3>
            <p>Travel between safari destinations by light aircraft flights or road transfers. Flights are commonly used between major parks such as the Serengeti and other regions.</p>
          </div>
          <div className="tanzania-how-card">
            <div className="tanzania-how-number">04</div>
            <h3>Safari &amp; Zanzibar Combo</h3>
            <p>One of the most popular itineraries — combine several days of safari with a beach stay in Zanzibar. After exploring the wilderness, relax on white-sand beaches and turquoise waters.</p>
          </div>
        </div>
      </section>

      {/* ===== PRACTICAL GUIDE ===== */}
      <section className="tanzania-practical">
        <div className="tanzania-section-header">
          <span className="section-tag centered">Before You Go</span>
          <h2>Practical Travel Guide</h2>
          <div className="tanzania-section-line" />
        </div>
        <div className="tanzania-practical-grid">
          <div className="tanzania-practical-card">
            <div className="tanzania-practical-icon"><FaPassport /></div>
            <h3>Entry Requirements</h3>
            <p>Most travelers require a visa, obtainable online or on arrival depending on nationality. Passports must be valid for at least six months beyond the travel dates.</p>
          </div>
          <div className="tanzania-practical-card">
            <div className="tanzania-practical-icon"><FaSuitcase /></div>
            <h3>What to Pack</h3>
            <ul>
              <li>Lightweight clothing in neutral colors</li>
              <li>Light jacket for cooler mornings and evenings</li>
              <li>Comfortable walking shoes</li>
              <li>Sun protection (hat, sunglasses, sunscreen)</li>
              <li>Camera or binoculars for wildlife viewing</li>
            </ul>
          </div>
          <div className="tanzania-practical-card">
            <div className="tanzania-practical-icon"><FaSimCard /></div>
            <h3>SIM Cards &amp; Connectivity</h3>
            <p>Local SIM cards are available at airports and in major towns. Mobile coverage is generally good in cities and most safari areas.</p>
          </div>
          <div className="tanzania-practical-card">
            <div className="tanzania-practical-icon"><FaCreditCard /></div>
            <h3>Currency &amp; Payments</h3>
            <p>Credit cards are accepted at most safari lodges and hotels. Carrying some Tanzanian shillings or US dollars is useful for smaller purchases and tips.</p>
          </div>
        </div>
      </section>

      {/* ===== CTA BUTTONS ===== */}
      <section className="tanzania-cta">
        <div className="tanzania-cta-content">
          <h2>Ready to Experience Tanzania?</h2>
          <p>Let us craft your perfect Tanzanian adventure — from Serengeti safaris to Zanzibar beaches.</p>
          <div className="tanzania-cta-buttons">
            <Link to="/blogs" className="hero-cta secondary">
              Continue Exploring <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/contact" className="hero-cta primary">
              Design Your Trip <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tanzania;
